import { IFile, IPhoto } from '../../../types/files';
import { ICoordinate } from '../../../lib/map';

export enum SiteVisitQuestionType {
  map = 'map',
  text = 'text',
  number = 'number',
  select = 'select',
  photos = 'photos',
  files = 'files',
}

export enum SiteVisitQuestionCategoryType {
  house = 'house',
  landscape = 'landscape',
  power = 'power',
  water = 'water',
  sewer = 'sewer',
  hydrant = 'hydrant',
  driveway = 'driveway',
  documents = 'documents',
}

export enum SiteVisitMapType {
  single = 'single',
  multiple = 'multiple',
  line = 'line',
  polygon = 'polygon',
}

export enum SelectMode {
  single = 'single',
  multiple = 'multiple',
  tags = 'tags',
}

export interface IMapPoint {
  id: string;
  title: string;
  coordinate: ICoordinate;
  images?: IPhoto[];
  comment?: string;
}

export interface IMapResult {
  center: ICoordinate;
  points: IMapPoint[];
  distance?: number;
}

export interface ITextResult {
  value: string;
  comment?: string;
}

export interface INumberResult {
  value: number;
  comment?: string;
}

export interface ISelectResult {
  value: string | string[];
  comment?: string;
}

export interface IPhotosResult {
  photos: IPhoto[];
  comment?: string;
}

export interface IFilesResult {
  files: IFile[];
  comment?: string;
}

export type QuestionResultMap = {
  [SiteVisitQuestionType.map]: IMapResult;
  [SiteVisitQuestionType.text]: ITextResult;
  [SiteVisitQuestionType.number]: INumberResult;
  [SiteVisitQuestionType.select]: ISelectResult;
  [SiteVisitQuestionType.photos]: IPhotosResult;
  [SiteVisitQuestionType.files]: IFilesResult;
};

export type PolyResult =
  | IMapResult
  | ITextResult
  | INumberResult
  | ISelectResult
  | IPhotosResult
  | IFilesResult;

export interface ISiteVisitQuestionBase {
  id?: string;
  title: string;
  description?: string;
  type: SiteVisitQuestionType;
  category: SiteVisitQuestionCategoryType;
  required: boolean;
  help?: string;
  helpImages?: string[];
  result?: PolyResult;
}

export interface IMapSiteVisitQuestion extends ISiteVisitQuestionBase {
  type: SiteVisitQuestionType.map;
  mapType: SiteVisitMapType;
  colors: {
    point: string;
    line: string;
    fill: string;
  };
  result?: IMapResult;
}

export interface ITextSiteVisitQuestion extends ISiteVisitQuestionBase {
  type: SiteVisitQuestionType.text;
  result?: ITextResult;
}

export interface INumberSiteVisitQuestion extends ISiteVisitQuestionBase {
  type: SiteVisitQuestionType.number;
  result?: INumberResult;
}

export interface ISelectSiteVisitQuestion extends ISiteVisitQuestionBase {
  type: SiteVisitQuestionType.select;
  mode: SelectMode;
  options: string[];
  result?: ISelectResult;
}

export interface IPhotosSiteVisitQuestion extends ISiteVisitQuestionBase {
  type: SiteVisitQuestionType.photos;
  result?: IPhotosResult;
}

export interface IFilesSiteVisitQuestion extends ISiteVisitQuestionBase {
  type: SiteVisitQuestionType.files;
  result?: IFilesResult;
}

export type ISiteVisitQuestion =
  | IMapSiteVisitQuestion
  | ITextSiteVisitQuestion
  | INumberSiteVisitQuestion
  | ISelectSiteVisitQuestion
  | IPhotosSiteVisitQuestion
  | IFilesSiteVisitQuestion;

export interface ISiteVisitCategory {
  id?: string;
  title: string;
  description?: string;
  type: SiteVisitQuestionCategoryType;
  icon: string;
  questions: ISiteVisitQuestion[];
}

export interface IMap {
  address: string;
  coordinates: [number, number];
  city?: string;
  state?: string;
  county?: string;
}

export interface ISiteVisit {
  id?: string;
  title: string;
  map?: IMap;
  userId?: string;
  preview?: IPhoto;
  categories: ISiteVisitCategory[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ISiteVisitPosition {
  categoryIndex: number;
  questionIndex: number;
}

export interface ICategoryProgress {
  category: ISiteVisitCategory;
  completed: number;
  total: number;
  percentage: number;
}

export interface IQuestionProgress {
  question: ISiteVisitQuestion;
  isCompleted: boolean;
  index: number;
}

export function isMapQuestion(question: ISiteVisitQuestion): question is IMapSiteVisitQuestion {
  return question.type === SiteVisitQuestionType.map;
}

export function isTextQuestion(question: ISiteVisitQuestion): question is ITextSiteVisitQuestion {
  return question.type === SiteVisitQuestionType.text;
}

export function isNumberQuestion(
  question: ISiteVisitQuestion
): question is INumberSiteVisitQuestion {
  return question.type === SiteVisitQuestionType.number;
}

export function isSelectQuestion(
  question: ISiteVisitQuestion
): question is ISelectSiteVisitQuestion {
  return question.type === SiteVisitQuestionType.select;
}

export function isPhotosQuestion(
  question: ISiteVisitQuestion
): question is IPhotosSiteVisitQuestion {
  return question.type === SiteVisitQuestionType.photos;
}

export function isFilesQuestion(question: ISiteVisitQuestion): question is IFilesSiteVisitQuestion {
  return question.type === SiteVisitQuestionType.files;
}

/**
 * Gets the typed result for a question
 * @param question The question to get the result for
 * @returns The typed result or undefined if no result exists
 */
export function getTypedResult<T extends SiteVisitQuestionType>(
  question: ISiteVisitQuestion,
  expectedType: T
): QuestionResultMap[T] | undefined {
  if (question.type === expectedType) {
    return question.result as QuestionResultMap[T];
  }
  return undefined;
}
